import type { Announcement } from "@/hooks/useAnnouncements";

/**
 * État « annonce de cette ouverture d'app », tenu en PORTÉE MODULE.
 *
 * ── Pourquoi ce module existe ────────────────────────────────────────────────
 * La modale d'annonce doit respecter deux règles qui tirent en sens inverse :
 *   1. une vue est comptée À L'AFFICHAGE, exactement une fois par ouverture ;
 *   2. la modale doit rester lisible tant que la personne ne l'a pas fermée.
 *
 * Le premier jet gardait (1) dans `sessionStorage` et (2) dans un `useState`.
 * Or `ProtectedRoute` (ligne 16) rend un sablier tant que
 * `loading || (!initialAuthDone && checkingRole)` : il DÉMONTE tout le
 * sous-arbre du tableau de bord pendant que l'authentification se stabilise,
 * puis le remonte. Au remontage, (2) repartait à zéro pendant que (1), lui,
 * survivait : la modale disparaissait toute seule, la vue étant déjà comptée.
 * La personne perdait une de ses trois vues sans avoir lu l'annonce. Reproduit
 * au navigateur 1 fois sur 5 connexions fraîches le 02-09.
 *
 * ── Pourquoi la portée module, et pas une deuxième clé de stockage ───────────
 * Sérialiser l'annonce affichée dans une seconde clé `sessionStorage` était la
 * correction évidente. Elle est piégée : les accès au stockage avalent leurs
 * erreurs par construction (Safari en navigation privée, stockage désactivé,
 * quota). Si l'écriture échoue en silence, plus rien ne retient l'effet, il se
 * redéclenche à CHAQUE remontage, et `record_announcement_view` incrémente à
 * nouveau (cette fonction SQL n'a AUCUNE fenêtre de déduplication). On
 * transformerait « une vue perdue de temps en temps » en « les trois vues
 * brûlées systématiquement », précisément pour les gens dont le stockage est
 * indisponible.
 *
 * Une instance de module a exactement la durée de vie qu'on veut :
 *   WEB   : une par onglet, détruite à la fermeture de l'onglet.
 *   NATIF : une par lancement de processus.
 * Elle survit au remontage d'un composant, elle ne peut ni jeter ni se
 * sérialiser, et elle n'ajoute aucune clé à effacer à la déconnexion.
 *
 * Le marqueur `sessionStorage` reste, mais pour une seule chose : survivre à un
 * RECHARGEMENT de page (F5), qui détruit le module. Il ne porte plus la
 * continuité de l'affichage.
 */

export const ANNOUNCEMENT_OPEN_KEY = "flowcut_announcement_seen_this_open";

type OpenDisplay = {
  id: string;
  row: Announcement;
  /** Fermée par la personne. On garde l'instantané, seul ce drapeau bloque le retour. */
  closed: boolean;
};

let display: OpenDisplay | null = null;

// Le stockage peut JETER (navigation privée Safari, stockage désactivé). Aucun
// accès ne doit remonter : au pire le marqueur est perdu, et la portée module
// assure quand même qu'on ne compte qu'une fois dans ce processus.
function safeGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignoré volontairement
  }
}

function safeRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignoré volontairement
  }
}

/** L'annonce affichée pendant cette ouverture, fermée ou non, ou `null`. */
export function getOpenDisplay(): OpenDisplay | null {
  return display;
}

/**
 * Réserve l'unique créneau d'affichage de cette ouverture.
 *
 * ⚠️ SEUL un `true` autorise à appeler `record_announcement_view`. « Compter
 * exactement une fois par ouverture » cesse ainsi d'être une propriété d'une
 * `ref` liée à un montage (qui repart à zéro au remontage) pour devenir une
 * propriété de ce module, testable directement.
 *
 * Synchrone de bout en bout, donc non entrelaçable : deux montages successifs
 * ne peuvent pas réserver tous les deux.
 */
export function claimAnnouncementForThisOpening(row: Announcement): boolean {
  // Déjà réservé dans CE processus. Ce test vient en premier : il tient même
  // quand le stockage est mort, et c'est lui qui empêche le double comptage.
  if (display) return false;
  // Déjà réservé avant un rechargement de page, qui a détruit le module.
  if (safeGet(ANNOUNCEMENT_OPEN_KEY)) return false;

  safeSet(ANNOUNCEMENT_OPEN_KEY, row.id);
  display = { id: row.id, row, closed: false };
  return true;
}

/**
 * La personne a fermé la modale.
 *
 * ⚠️ On ne remet PAS `display` à `null` : l'instantané doit rester pour que
 * Radix puisse jouer son animation de sortie, et pour qu'un remontage juste
 * après la fermeture ne rouvre pas ce qui vient d'être fermé. C'est `closed`
 * qui bloque le retour.
 */
export function markAnnouncementClosed(): void {
  if (display) display.closed = true;
}

/** Une annonce a-t-elle déjà occupé le créneau de cette ouverture ? */
export function hasSeenAnnouncementThisOpening(): boolean {
  return display !== null || safeGet(ANNOUNCEMENT_OPEN_KEY) !== null;
}

/**
 * Déconnexion, ou session expirée : l'ouverture repart à zéro.
 *
 * Indispensable sur NATIF, où la sortie est une navigation SPA : même
 * processus, donc même instance de module. Sans ça, la personne suivante sur
 * la tablette d'accueil du salon trouverait `closed === true` et ne verrait
 * jamais aucune annonce.
 */
export function resetAnnouncementOpenState(): void {
  display = null;
  safeRemove(ANNOUNCEMENT_OPEN_KEY);
}
